/**
 * Staff Folder Page
 * Each staff has their own document folder
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  FolderOpen, Upload, FileText, Image, File, Trash2, Download, 
  Search, Filter, User, ChevronRight, X, Eye, Plus
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DOCUMENT_CATEGORIES = [
  'IC/Passport', 'Resume/CV', 'Offer Letter', 'Contract', 'Certificate',
  'Payslip', 'EPF Statement', 'Medical Report', 'Warning Letter', 'Promotion Letter', 'Other'
];

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

const getFileIcon = (fileType) => {
  if (['.jpg', '.jpeg', '.png', '.gif'].includes(fileType)) return <Image className="w-5 h-5 text-green-600" />;
  if (['.pdf'].includes(fileType)) return <FileText className="w-5 h-5 text-red-600" />;
  if (['.doc', '.docx'].includes(fileType)) return <FileText className="w-5 h-5 text-blue-600" />;
  return <File className="w-5 h-5 text-gray-600" />;
};

const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizeClass = size === 'lg' ? 'max-w-4xl' : size === 'xl' ? 'max-w-6xl' : 'max-w-lg';
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-xl w-full ${sizeClass} max-h-[90vh] overflow-hidden flex flex-col`}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ isOpen, onClose, staffId, staffName, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState('Other');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const handleFilesSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    // Filter valid file types
    const validTypes = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif'];
    const validFiles = newFiles.filter(file => {
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      return validTypes.includes(ext);
    });
    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and Drop Handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    
    let uploaded = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', category);
        formData.append('description', description);
        
        await axios.post(`${API}/staff-folder/${staffId}/upload?category=${encodeURIComponent(category)}&description=${encodeURIComponent(description)}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded++;
        setProgress(Math.round((uploaded / files.length) * 100));
      } catch (error) {
        console.error('Upload failed:', file.name, error);
      }
    }
    
    setUploading(false);
    setFiles([]);
    setProgress(0);
    onUploadComplete();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Upload Documents - ${staffName}`}>
      <div className="space-y-4" data-testid="upload-modal">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="upload-category-select"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg">
            {DOCUMENT_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. 2024 Contract Renewal"
            data-testid="upload-description-input"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>

        <div 
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
            isDragging 
              ? 'border-red-500 bg-red-50 scale-[1.02]' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          data-testid="file-drop-zone"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <label className="cursor-pointer">
            <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif" 
              onChange={handleFilesSelect} className="hidden" data-testid="file-input" />
            <Upload className={`w-12 h-12 mx-auto mb-2 transition-colors ${isDragging ? 'text-red-500' : 'text-gray-400'}`} />
            <p className={`text-sm font-medium ${isDragging ? 'text-red-600' : 'text-gray-600'}`}>
              {isDragging ? 'Drop files here!' : 'Drag & drop files here'}
            </p>
            <p className="text-xs text-gray-500 mt-1">or click to browse</p>
            <p className="text-xs text-gray-400 mt-2">PDF, Word, Images (Max 10MB each)</p>
          </label>
        </div>

        {files.length > 0 && (
          <div className="space-y-2 max-h-40 overflow-y-auto" data-testid="selected-files-list">
            {files.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                <div className="flex items-center gap-2">
                  {getFileIcon(file.name.substring(file.name.lastIndexOf('.')))}
                  <span className="text-sm text-gray-700 truncate max-w-[200px]">{file.name}</span>
                  <span className="text-xs text-gray-400">{formatFileSize(file.size)}</span>
                </div>
                <button onClick={() => removeFile(idx)} className="text-red-500 hover:text-red-700" data-testid={`remove-file-${idx}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {uploading && (
          <div className="bg-blue-50 p-3 rounded-lg" data-testid="upload-progress">
            <div className="flex justify-between text-sm text-blue-700 mb-1">
              <span>Uploading...</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" data-testid="cancel-upload-btn">
            Cancel
          </button>
          <button onClick={handleUpload} disabled={files.length === 0 || uploading}
            data-testid="submit-upload-btn"
            className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50">
            {uploading ? 'Uploading...' : `Upload ${files.length} File(s)`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Document Preview Modal
const PreviewModal = ({ isOpen, onClose, document }) => {
  if (!document) return null;

  const isImage = ['.jpg', '.jpeg', '.png', '.gif'].includes(document.file_type);
  const isPdf = document.file_type === '.pdf';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={document.filename} size="lg">
      <div className="space-y-4">
        {isImage && document.file_data && (
          <img 
            src={`data:${document.content_type};base64,${document.file_data}`} 
            alt={document.filename}
            className="max-w-full max-h-[60vh] mx-auto rounded-lg"
          />
        )}
        {isPdf && (
          <div className="text-center py-8">
            <FileText className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <p className="text-gray-600">PDF Preview not available. Click download to view.</p>
          </div>
        )}
        {!isImage && !isPdf && (
          <div className="text-center py-8">
            <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Preview not available for this file type.</p>
          </div>
        )}
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500">Category:</span> <span className="font-medium">{document.category}</span></div>
            <div><span className="text-gray-500">Size:</span> <span className="font-medium">{formatFileSize(document.file_size)}</span></div>
            <div><span className="text-gray-500">Uploaded:</span> <span className="font-medium">{new Date(document.uploaded_at).toLocaleDateString()}</span></div>
            <div><span className="text-gray-500">Type:</span> <span className="font-medium">{document.file_type}</span></div>
          </div>
          {document.description && (
            <p className="mt-2 text-sm"><span className="text-gray-500">Description:</span> {document.description}</p>
          )}
        </div>

        {document.file_data && (
          <a 
            href={`data:${document.content_type};base64,${document.file_data}`}
            download={document.filename}
            className="block w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-center"
          >
            <Download className="w-4 h-4 inline mr-2" /> Download File
          </a>
        )}
      </div>
    </Modal>
  );
};

const StaffFolderPage = () => {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [folderSummaries, setFolderSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  // Fetch staff list
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [staffRes, summaryRes] = await Promise.all([
          axios.get(`${API}/staff`),
          axios.get(`${API}/staff-folder/summary/all`)
        ]);
        setStaffList(staffRes.data || []);
        setFolderSummaries(summaryRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch documents when staff selected
  const fetchStaffDocuments = useCallback(async (staffId) => {
    try {
      const query = filterCategory ? `?category=${encodeURIComponent(filterCategory)}` : '';
      const response = await axios.get(`${API}/staff-folder/${staffId}${query}`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    }
  }, [filterCategory]);

  useEffect(() => {
    if (selectedStaff) {
      fetchStaffDocuments(selectedStaff.id);
    }
  }, [selectedStaff, filterCategory, fetchStaffDocuments]);

  // Get document count for staff
  const getDocCount = (staffId) => {
    const summary = folderSummaries.find(s => s.staff_id === staffId);
    return summary?.document_count || 0;
  };

  // Filter staff
  const filteredStaff = staffList.filter(s => {
    const matchSearch = s.name?.toLowerCase().includes(search.toLowerCase());
    const matchDept = !filterDept || s.department === filterDept;
    return matchSearch && matchDept;
  });

  const departments = [...new Set(staffList.map(s => s.department).filter(Boolean))];

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/staff-folder/${selectedStaff.id}/document/${docId}`);
      fetchStaffDocuments(selectedStaff.id);
    } catch (error) {
      alert('Failed to delete document');
    }
  };

  const handlePreviewDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/staff-folder/${selectedStaff.id}/document/${doc.id}`);
      setPreviewDoc(response.data);
    } catch (error) {
      alert('Failed to load document');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-folder-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FolderOpen className="w-7 h-7 text-amber-600" />
            Staff Folder
          </h1>
          <p className="text-gray-500 text-sm">Document management for each staff member</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Staff List Panel */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search staff..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
                data-testid="staff-search-input"
              />
            </div>
            <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="department-filter">
              <option value="">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          <div className="max-h-[60vh] overflow-y-auto" data-testid="staff-list">
            {filteredStaff.map(staff => (
              <button
                key={staff.id}
                onClick={() => setSelectedStaff(staff)}
                data-testid={`staff-item-${staff.id}`}
                className={`w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                  selectedStaff?.id === staff.id ? 'bg-red-50 border-l-4 border-l-red-600' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold">
                      {staff.name?.charAt(0) || 'S'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{staff.name}</p>
                      <p className="text-xs text-gray-500">{staff.department || 'No Dept'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs" data-testid={`doc-count-${staff.id}`}>
                      {getDocCount(staff.id)} docs
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Documents Panel */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {selectedStaff ? (
            <>
              {/* Staff Header */}
              <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center text-red-700 font-bold text-xl">
                      {selectedStaff.name?.charAt(0)}
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-800">{selectedStaff.name}</h2>
                      <p className="text-sm text-gray-500">{selectedStaff.position || selectedStaff.department}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setUploadModalOpen(true)}
                    className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2"
                    data-testid="upload-document-btn"
                  >
                    <Plus className="w-4 h-4" /> Upload
                  </button>
                </div>
              </div>

              {/* Filter */}
              <div className="p-4 border-b border-gray-100 flex items-center gap-4">
                <Filter className="w-4 h-4 text-gray-500" />
                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  data-testid="category-filter">
                  <option value="">All Categories</option>
                  {DOCUMENT_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
                <span className="text-sm text-gray-500">{documents.length} document(s)</span>
              </div>

              {/* Documents Grid */}
              <div className="p-4 max-h-[50vh] overflow-y-auto" data-testid="documents-panel">
                {documents.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4" data-testid="documents-grid">
                    {documents.map(doc => (
                      <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-testid={`document-card-${doc.id}`}>
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            {getFileIcon(doc.file_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 text-sm truncate">{doc.filename}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {doc.category} • {formatFileSize(doc.file_size)}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(doc.uploaded_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                          <button onClick={() => handlePreviewDocument(doc)}
                            data-testid={`view-doc-${doc.id}`}
                            className="flex-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center justify-center gap-1">
                            <Eye className="w-3 h-3" /> View
                          </button>
                          <button onClick={() => handleDeleteDocument(doc.id)}
                            data-testid={`delete-doc-${doc.id}`}
                            className="px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded flex items-center justify-center gap-1">
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12" data-testid="empty-documents">
                    <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No documents uploaded yet</p>
                    <button onClick={() => setUploadModalOpen(true)}
                      data-testid="upload-first-doc-btn"
                      className="mt-4 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 text-sm">
                      Upload First Document
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full py-20">
              <div className="text-center">
                <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Select a staff member to view their folder</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        staffId={selectedStaff?.id}
        staffName={selectedStaff?.name}
        onUploadComplete={() => {
          fetchStaffDocuments(selectedStaff.id);
          // Refresh summaries
          axios.get(`${API}/staff-folder/summary/all`).then(res => setFolderSummaries(res.data || []));
        }}
      />

      {/* Preview Modal */}
      <PreviewModal
        isOpen={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        document={previewDoc}
      />
    </div>
  );
};

export default StaffFolderPage;
